import { useQuery } from '@tanstack/react-query';

import { PRICE_RANGE_OPTIONS, STATUS_OPTIONS } from '@/components/utils/enums';
import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import { IHaggleFilters } from '@/interfaces/api/product/IHaggleFilters';
import { FilterStateSideBar } from '@/interfaces/home/IFilterStateSideBar';
import { haggleApiService } from '@/services/haggle.service';

interface FiltersSectionProps {
	onFiltersChange?: (filters: FilterStateSideBar) => void;
	filters: FilterStateSideBar;
	onFiltersUpdate: (filters: FilterStateSideBar) => void;
}

const FiltersSection = ({
	onFiltersChange,
	filters,
	onFiltersUpdate,
}: FiltersSectionProps) => {
	// Fetch stored filters from backend
	const { data: storedFilters, isLoading: filtersLoading } =
		useQuery<IHaggleFilters>({
			queryKey: [ApiQueryKeys.getAllHaggleStoredFilters],
			queryFn: () => haggleApiService.getAllHaggleStoredFilters(),
			refetchOnWindowFocus: false,
		});

	// Use predefined price ranges and statuses from constants
	const priceRangeOptions = PRICE_RANGE_OPTIONS;
	const statusOptions = STATUS_OPTIONS;

	// Filter handlers
	const handleFilterChange = (
		filterType: keyof FilterStateSideBar,
		value: string,
		checked: boolean,
	) => {
		const newFilters = {
			...filters,
			[filterType]: checked
				? [
						...(filters[filterType as keyof FilterStateSideBar] as string[]),
						value,
				  ]
				: (filters[filterType as keyof FilterStateSideBar] as string[]).filter(
						(item) => item !== value,
				  ),
		};
		onFiltersUpdate(newFilters);
		onFiltersChange?.(newFilters);
	};

	const clearAllFilters = () => {
		const newFilters = {
			productTypes: [],
			vendors: [],
			priceRanges: [],
			statuses: [],
		};
		onFiltersUpdate(newFilters);
		onFiltersChange?.(newFilters);
	};

	return (
		<div className="border-y border-gray-200 my-2 pt-1 sm:pt-2">
			<h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
				Filters
			</h3>

			{filtersLoading ? (
				<div className="flex justify-center py-4">
					<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purpleBrand-500"></div>
				</div>
			) : (
				<>
					{/* Product Type Filter */}
					{storedFilters?.productTypes &&
						storedFilters.productTypes.length > 0 && (
							<div className="mb-4">
								<div className="flex items-center justify-between mb-2">
									<span className="text-sm font-medium text-gray-700">
										Product Type
									</span>
									<span className="text-xs text-gray-500">
										({storedFilters.productTypes.length})
									</span>
								</div>
								<div className="space-y-2">
									{storedFilters.productTypes.map((type) => (
										<label key={type} className="flex items-center">
											<input
												type="checkbox"
												checked={filters.productTypes.includes(type)}
												onChange={(e) =>
													handleFilterChange(
														'productTypes',
														type,
														e.target.checked,
													)
												}
												className="rounded border-gray-300 text-purpleBrand-500 focus:ring-purpleBrand-500"
											/>
											<span className="ml-2 text-sm text-gray-600">
												{type || 'Uncategorized'}
											</span>
										</label>
									))}
								</div>
							</div>
						)}

					{/* Vendors Filter */}
					{storedFilters?.vendors && storedFilters.vendors.length > 0 && (
						<div className="mb-4">
							<div className="flex items-center justify-between mb-2">
								<span className="text-sm font-medium text-gray-700">
									Merchants
								</span>
								<span className="text-xs text-gray-500">
									({storedFilters.vendors.length})
								</span>
							</div>
							<div className="space-y-2">
								{storedFilters.vendors.map((vendor) => (
									<label key={vendor} className="flex items-center">
										<input
											type="checkbox"
											checked={filters.vendors.includes(vendor)}
											onChange={(e) =>
												handleFilterChange('vendors', vendor, e.target.checked)
											}
											className="rounded border-gray-300 text-purpleBrand-500 focus:ring-purpleBrand-500"
										/>
										<span className="ml-2 text-sm text-gray-600">{vendor}</span>
									</label>
								))}
							</div>
						</div>
					)}

					{/* Price Range Filter */}
					<div className="mb-4">
						<div className="flex items-center justify-between mb-2">
							<span className="text-sm font-medium text-gray-700">
								Price Range
							</span>
						</div>
						<div className="space-y-2">
							{priceRangeOptions.map((option) => (
								<label key={option.value} className="flex items-center">
									<input
										type="checkbox"
										checked={filters.priceRanges.includes(option.value)}
										onChange={(e) =>
											handleFilterChange(
												'priceRanges',
												option.value,
												e.target.checked,
											)
										}
										className="rounded border-gray-300 text-purpleBrand-500 focus:ring-purpleBrand-500"
									/>
									<span className="ml-2 text-sm text-gray-600">
										{option.label}
									</span>
								</label>
							))}
						</div>
					</div>

					{/* Status Filter */}
					<div className="mb-4">
						<div className="flex items-center justify-between mb-2">
							<span className="text-sm font-medium text-gray-700">Status</span>
						</div>
						<div className="space-y-2">
							{statusOptions.map((option) => (
								<label key={option.value} className="flex items-center">
									<input
										type="checkbox"
										checked={filters.statuses.includes(option.value)}
										onChange={(e) =>
											handleFilterChange(
												'statuses',
												option.value,
												e.target.checked,
											)
										}
										className="rounded border-gray-300 text-purpleBrand-500 focus:ring-purpleBrand-500"
									/>
									<span className="ml-2 text-sm text-gray-600">
										{option.label}
									</span>
								</label>
							))}
						</div>
					</div>
				</>
			)}

			{/* Filter Action Buttons */}
			<div className="flex gap-2 justify-center my-2">
				<button
					onClick={clearAllFilters}
					className="w-1/2 py-2.5 sm:py-2 px-3 sm:px-4 text-xs font-medium text-purpleBrand-500 border border-purpleBrand-500 rounded-md hover:bg-purpleBrand-600 hover:text-white transition-colors"
				>
					Reset
				</button>
			</div>
		</div>
	);
};

export default FiltersSection;
